import React, { useState, useEffect } from 'react';
import {
    Phone,
    PhoneOff,
    Loader2,
    AlertCircle,
    CheckCircle2,
    Search,
    Settings2,
    Save,
    Pause,
    Play,
} from 'lucide-react';
import {
    getVoiceBookingApi,
    getAvailableNumbersApi,
    provisionNumberApi,
    updateVoiceBookingSettingsApi,
    setVoiceBookingEnabledApi,
    releaseNumberApi,
} from '../services/voiceBookingApi';

/**
 * AI phone-booking card for the Organisation settings page.
 *
 * Two jobs:
 *   1. Assign the workshop a phone number from the shared pool (and release it).
 *   2. Configure which booking attributes the bot collects over the phone.
 *
 * Numbers are pre-purchased and pre-wired by infrastructure, so assigning one is
 * instant — there is no provisioning wait.
 *
 * `available: false` on the response means this deployment has no Amazon Connect
 * instance configured (e.g. local dev), which is shown as "not available here"
 * rather than as an error.
 */

/** Format +61291644232 as +61 2 9164 4232 for display only. */
const formatPhone = (e164) => {
    if (!e164) return '';
    const m = /^\+61(\d)(\d{4})(\d{4})$/.exec(e164);
    return m ? `+61 ${m[1]} ${m[2]} ${m[3]}` : e164;
};

const VoiceBookingCard = () => {
    const [config, setConfig] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isBusy, setIsBusy] = useState(false);
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);

    // number picker
    const [numbers, setNumbers] = useState(null); // null = not searched yet
    const [picked, setPicked] = useState('');

    // attribute settings (local draft until saved)
    const [showSettings, setShowSettings] = useState(false);
    const [draft, setDraft] = useState(null);

    const load = async () => {
        setError(null);
        try {
            const data = await getVoiceBookingApi();
            setConfig(data);
            setDraft(toDraft(data));
        } catch (err) {
            if (err.response?.status === 403) {
                setError('You do not have permission to manage phone bookings.');
            } else {
                setError(err.response?.data?.message || 'Failed to load phone booking settings.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const toDraft = (data) => ({
        askPreferredTime: !!data?.askPreferredTime,
        askNotes: !!data?.askNotes,
        phoneBookableServiceTypes: data?.phoneBookableServiceTypes || [],
        defaultServiceType: data?.defaultServiceType || '',
        defaultDurationMinutes: data?.defaultDurationMinutes ?? '',
    });

    const run = async (fn, successMessage) => {
        setIsBusy(true);
        setError(null);
        setNotice(null);
        try {
            const updated = await fn();
            setConfig(updated);
            setDraft(toDraft(updated));
            if (successMessage) setNotice(successMessage);
            return true;
        } catch (err) {
            setError(err.response?.data?.message || 'Something went wrong. Please try again.');
            return false;
        } finally {
            setIsBusy(false);
        }
    };

    const handleFindNumbers = async () => {
        setIsBusy(true);
        setError(null);
        setNotice(null);
        try {
            const result = await getAvailableNumbersApi();
            setNumbers(result.phoneNumbers || []);
            setPicked(result.phoneNumbers?.[0] || '');
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to look up available numbers.');
        } finally {
            setIsBusy(false);
        }
    };

    const handleProvision = async () => {
        if (!picked) return;
        const ok = await run(() => provisionNumberApi(picked), `${formatPhone(picked)} is now your booking line.`);
        if (ok) {
            setNumbers(null);
            setPicked('');
        }
    };

    const handleRelease = async () => {
        if (!window.confirm(
            'Release this number back to the pool? Customers calling it will no longer reach your booking line, '
            + 'and you may not get the same number back.'
        )) {
            return;
        }
        await run(() => releaseNumberApi(), 'Number released.');
    };

    const handleToggleEnabled = async () => {
        const next = !config.enabled;
        await run(
            () => setVoiceBookingEnabledApi(next),
            next ? 'Phone bookings resumed.' : 'Phone bookings paused. Your number is still reserved.',
        );
    };

    const handleSaveSettings = async () => {
        await run(() => updateVoiceBookingSettingsApi({
            askPreferredTime: draft.askPreferredTime,
            askNotes: draft.askNotes,
            phoneBookableServiceTypes: draft.phoneBookableServiceTypes,
            defaultServiceType: draft.defaultServiceType || null,
            defaultDurationMinutes: draft.defaultDurationMinutes === ''
                ? null
                : Number(draft.defaultDurationMinutes),
        }), 'Booking settings saved.');
    };

    const toggleServiceType = (value) => {
        setDraft((d) => {
            const has = d.phoneBookableServiceTypes.includes(value);
            const next = has
                ? d.phoneBookableServiceTypes.filter((v) => v !== value)
                : [...d.phoneBookableServiceTypes, value];
            // A default that is no longer offerable would be unreachable — clear it.
            const defaultStillValid = next.length === 0 || next.includes(d.defaultServiceType);
            return {
                ...d,
                phoneBookableServiceTypes: next,
                defaultServiceType: defaultStillValid ? d.defaultServiceType : '',
            };
        });
    };

    // ── Render ──────────────────────────────────────────────────────────────
    if (isLoading) {
        return (
            <div className="bg-white rounded-2xl border border-gray-200 p-6">
                <div className="flex items-center gap-2 text-gray-400">
                    <Loader2 size={16} className="animate-spin" />
                    <span className="text-sm">Loading phone bookings…</span>
                </div>
            </div>
        );
    }

    const hasNumber = !!config?.phoneNumber;
    const featureAvailable = !!config?.available;
    const options = config?.serviceTypeOptions || [];
    // Empty selection means "no restriction" on the backend.
    const allTypesOffered = (draft?.phoneBookableServiceTypes?.length ?? 0) === 0;

    return (
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                    <div className="w-9 h-9 bg-violet-50 rounded-lg flex items-center justify-center">
                        <Phone size={16} className="text-violet-600" />
                    </div>
                    <h3 className="font-semibold text-gray-900">Phone bookings</h3>
                </div>
                {hasNumber && (
                    config.live ? (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full bg-green-100 text-green-800">
                            <CheckCircle2 size={12} /> Taking calls
                        </span>
                    ) : (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full bg-amber-100 text-amber-800">
                            Paused
                        </span>
                    )
                )}
            </div>

            {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2 text-sm text-red-700">
                    <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
                    <span>{error}</span>
                </div>
            )}

            {notice && (
                <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-start gap-2 text-sm text-green-800">
                    <CheckCircle2 size={16} className="flex-shrink-0 mt-0.5" />
                    <span>{notice}</span>
                </div>
            )}

            {!featureAvailable && (
                <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-lg flex items-start gap-2 text-sm text-gray-600">
                    <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
                    <span>Phone bookings are not available on this environment.</span>
                </div>
            )}

            {hasNumber ? (
                <>
                    <div className="mb-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                        <div className="text-xs font-medium text-gray-500 mb-1">Your booking line</div>
                        <div className="text-lg font-semibold text-gray-900 tracking-tight">
                            {formatPhone(config.phoneNumber)}
                        </div>
                        <p className="text-xs text-gray-500 mt-2">
                            Give this number to customers. Calls are answered by your AI booking assistant.
                        </p>
                    </div>

                    <div className="flex items-center gap-3 mb-2">
                        <button
                            onClick={handleToggleEnabled}
                            disabled={isBusy}
                            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-white bg-violet-600 hover:bg-violet-700 transition-colors disabled:opacity-50"
                        >
                            {isBusy
                                ? <Loader2 size={16} className="animate-spin" />
                                : (config.enabled ? <Pause size={16} /> : <Play size={16} />)}
                            {config.enabled ? 'Pause' : 'Resume'}
                        </button>
                        <button
                            onClick={() => setShowSettings((s) => !s)}
                            disabled={isBusy}
                            className="inline-flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900 disabled:opacity-50"
                        >
                            <Settings2 size={14} /> What the bot asks
                        </button>
                        <button
                            onClick={handleRelease}
                            disabled={isBusy}
                            className="inline-flex items-center gap-2 text-sm font-medium text-red-600 hover:text-red-700 disabled:opacity-50 ml-auto"
                        >
                            <PhoneOff size={14} /> Release
                        </button>
                    </div>
                </>
            ) : (
                <>
                    <p className="text-sm text-gray-600 mb-4">
                        Give your customers a phone number they can call to book. An AI assistant
                        answers, takes the booking details and adds it to your diary.
                    </p>

                    {numbers === null ? (
                        <button
                            onClick={handleFindNumbers}
                            disabled={isBusy || !featureAvailable}
                            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-white bg-violet-600 hover:bg-violet-700 transition-colors disabled:opacity-50"
                        >
                            {isBusy ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
                            Find a number
                        </button>
                    ) : numbers.length === 0 ? (
                        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                            No numbers are free at the moment. Please contact support and we will add more
                            to the pool.
                        </div>
                    ) : (
                        <div className="space-y-3">
                            <label className="block">
                                <span className="text-xs font-medium text-gray-500">Choose a number</span>
                                <select
                                    value={picked}
                                    onChange={(e) => setPicked(e.target.value)}
                                    className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                                >
                                    {numbers.map((n) => (
                                        <option key={n} value={n}>{formatPhone(n)}</option>
                                    ))}
                                </select>
                            </label>
                            <button
                                onClick={handleProvision}
                                disabled={isBusy || !picked}
                                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-white bg-violet-600 hover:bg-violet-700 transition-colors disabled:opacity-50"
                            >
                                {isBusy ? <Loader2 size={16} className="animate-spin" /> : <Phone size={16} />}
                                Use this number
                            </button>
                        </div>
                    )}
                </>
            )}

            {/* ── Booking attributes ─────────────────────────────────────── */}
            {showSettings && draft && (
                <div className="mt-5 pt-5 border-t border-gray-200 space-y-4">
                    <p className="text-xs text-gray-500">
                        The bot always confirms the customer, their vehicle and a date. These control
                        what else it asks for.
                    </p>

                    <label className="flex items-start gap-3 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={draft.askPreferredTime}
                            onChange={(e) => setDraft({ ...draft, askPreferredTime: e.target.checked })}
                            className="mt-0.5 rounded border-gray-300 text-violet-600 focus:ring-violet-500"
                        />
                        <span className="text-sm">
                            <span className="font-medium text-gray-900">Ask for a preferred time</span>
                            <span className="block text-xs text-gray-500">
                                Off means bookings arrive date-only for your team to slot in.
                            </span>
                        </span>
                    </label>

                    <label className="flex items-start gap-3 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={draft.askNotes}
                            onChange={(e) => setDraft({ ...draft, askNotes: e.target.checked })}
                            className="mt-0.5 rounded border-gray-300 text-violet-600 focus:ring-violet-500"
                        />
                        <span className="text-sm">
                            <span className="font-medium text-gray-900">Ask if there is anything else</span>
                            <span className="block text-xs text-gray-500">
                                Records a free-text note. Least reliable over the phone, so off by default.
                            </span>
                        </span>
                    </label>

                    <div>
                        <span className="text-xs font-medium text-gray-500">
                            Services customers can book by phone
                        </span>
                        <p className="text-xs text-gray-400 mb-2">
                            {allTypesOffered
                                ? 'Nothing selected — all your services can be booked by phone.'
                                : `${draft.phoneBookableServiceTypes.length} selected.`}
                        </p>
                        <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-2 space-y-1">
                            {options.map((opt) => (
                                <label key={opt.value} className="flex items-center gap-2 cursor-pointer px-1 py-0.5">
                                    <input
                                        type="checkbox"
                                        checked={draft.phoneBookableServiceTypes.includes(opt.value)}
                                        onChange={() => toggleServiceType(opt.value)}
                                        className="rounded border-gray-300 text-violet-600 focus:ring-violet-500"
                                    />
                                    <span className="text-sm text-gray-700">{opt.label}</span>
                                </label>
                            ))}
                        </div>
                    </div>

                    <label className="block">
                        <span className="text-xs font-medium text-gray-500">
                            Default service if the caller is unsure
                        </span>
                        <select
                            value={draft.defaultServiceType}
                            onChange={(e) => setDraft({ ...draft, defaultServiceType: e.target.value })}
                            className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                        >
                            <option value="">No default — the bot must ask</option>
                            {options
                                .filter((o) => allTypesOffered || draft.phoneBookableServiceTypes.includes(o.value))
                                .map((opt) => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                        </select>
                    </label>

                    <label className="block">
                        <span className="text-xs font-medium text-gray-500">
                            Default booking length (minutes)
                        </span>
                        <input
                            type="number"
                            min={1}
                            max={1440}
                            value={draft.defaultDurationMinutes}
                            onChange={(e) => setDraft({ ...draft, defaultDurationMinutes: e.target.value })}
                            placeholder="Leave blank for none"
                            className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                        />
                    </label>

                    <button
                        onClick={handleSaveSettings}
                        disabled={isBusy}
                        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-white bg-violet-600 hover:bg-violet-700 transition-colors disabled:opacity-50"
                    >
                        {isBusy ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                        Save settings
                    </button>
                </div>
            )}
        </div>
    );
};

export default VoiceBookingCard;
